import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, MagnifyingGlassIcon, UsersIcon, EyeIcon } from '@heroicons/react/24/outline';
import { STAGE_CONFIG, CRM_STAGES } from '../../redux/slices/crmSlice';

const CustomersSidebar = ({ isOpen, onClose, users, searchQuery, onSearchChange }) => {
    if (!isOpen) return null;

    const filteredUsers = users.filter(user => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.name?.toLowerCase().includes(query) ||
            user.number?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
        );
    });

    return (
        <>
            {/* Overlay */}
            <div 
                className='fixed inset-0 bg-black/50 z-40 transition-opacity'
                onClick={onClose}
            />
            
            {/* Sidebar */}
            <div className='fixed right-0 top-0 h-full w-full md:w-4/5 lg:w-3/5 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col'>
                {/* Sidebar Header */}
                <div className='bg-gradient-to-r from-purple-600 to-purple-700 p-6 flex items-center justify-between'>
                    <div>
                        <h2 className='text-2xl font-black text-white flex items-center gap-2'>
                            <UsersIcon className='h-7 w-7' />
                            All Customers
                        </h2>
                        <p className='text-purple-100 text-sm font-medium mt-1'>
                            Total: {users.length} customers
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className='p-2 hover:bg-purple-500 rounded-lg transition-colors'
                    >
                        <XMarkIcon className='h-6 w-6 text-white' />
                    </button>
                </div>

                {/* Search Bar */}
                <div className='p-4 border-b border-slate-200 bg-slate-50'>
                    <div className='relative'>
                        <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400' />
                        <input
                            type='text'
                            placeholder='Search by name, mobile, or email...'
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className='w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                        />
                    </div>
                </div>

                {/* Table Container */}
                <div className='flex-1 overflow-auto p-4'>
                    <div className='bg-white rounded-xl border border-slate-200 overflow-hidden'>
                        <div className='overflow-x-auto'>
                            <table className='w-full'>
                                <thead className='bg-slate-50 border-b border-slate-200'>
                                    <tr>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Name</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Mobile</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Email</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Stage</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Services</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Lead Type</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Created</th>
                                        <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-slate-100'>
                                    {filteredUsers.map((user) => {
                                        const stageConfig = STAGE_CONFIG[user.stage] || STAGE_CONFIG[CRM_STAGES.NEW_LEAD];
                                        const services = Array.isArray(user.services) ? user.services.join(', ') : (user.services || 'N/A');
                                        const createdDate = user.createdAt || user.api_created_at || user.created_at;
                                        
                                        return (
                                            <tr key={user.id} className='hover:bg-slate-50 transition-colors'>
                                                <td className='px-4 py-3 text-sm font-semibold text-slate-900'>
                                                    {user.name || 'N/A'}
                                                </td>
                                                <td className='px-4 py-3 text-sm text-slate-600'>
                                                    {user.number || 'N/A'}
                                                </td>
                                                <td className='px-4 py-3 text-sm text-slate-600'>
                                                    {user.email || 'N/A'}
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${stageConfig.color} bg-opacity-10`}>
                                                        {stageConfig.title}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3 text-xs text-slate-600 max-w-xs truncate'>
                                                    {services}
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                                        user.leadType === 'business' 
                                                            ? 'text-purple-700 bg-purple-100' 
                                                            : 'text-blue-700 bg-blue-100'
                                                    }`}>
                                                        {user.leadType || 'Service'}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3 text-xs text-slate-600'>
                                                    {createdDate ? new Date(createdDate).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <button
                                                        onClick={() => {
                                                            window.open(`/admin/user-details/${user.id}`, '_blank');
                                                        }}
                                                        className='p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors'
                                                        title='View Details'
                                                    >
                                                        <EyeIcon className='h-4 w-4' />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            
                            {/* No Results */}
                            {filteredUsers.length === 0 && (
                                <div className='text-center py-12'>
                                    <UsersIcon className='h-12 w-12 text-slate-300 mx-auto mb-3' />
                                    <p className='text-slate-500 font-medium'>No customers found</p>
                                    <p className='text-slate-400 text-sm mt-1'>Try adjusting your search</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomersSidebar;
